import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Rocket } from 'lucide-react';
import { adminApi, devicesApi, releasesApi, updatesApi, type Device, type Project } from '../api';
import { useAuth } from '../auth';
import { useAsync, useProjectSelection } from '../hooks';
import ProjectPicker from '../components/ProjectPicker';
import DeviceModal from '../components/DeviceModal';
import { DeviceStateBadge, HealthBadge } from '../components/StatusBadges';
import { Alert, Button, Checkbox, Empty, Field, Modal, PageHeader, Select, Table, TextArea, errorText, timeAgo } from '../components/ui';

export default function DevicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { projects, list, selected, unassigned, select } = useProjectSelection({ allowUnassigned: isAdmin });
  const [groupId, setGroupId] = useState('');
  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [openDevice, setOpenDevice] = useState<Device | null>(null);
  const [updateScope, setUpdateScope] = useState<'selected' | 'all' | null>(null);

  const projectId = selected?.id;
  const devices = useAsync(
    () =>
      unassigned
        ? devicesApi.list({ unassigned: true })
        : projectId
          ? devicesApi.list({ project_id: projectId })
          : Promise.resolve([]),
    [projectId, unassigned],
  );
  const health = useAsync(adminApi.health, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (devices.data ?? []).filter(
      (d) =>
        (!groupId || String(d.group_id) === groupId) &&
        (!q || d.serial_number.toLowerCase().includes(q) || d.name.toLowerCase().includes(q)),
    );
  }, [devices.data, groupId, query]);

  const changeProject = (value: string) => {
    select(value);
    setGroupId('');
    setChecked(new Set());
  };

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allChecked = visible.length > 0 && visible.every((d) => checked.has(d.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(visible.map((d) => d.id)));

  const refresh = () => {
    devices.reload();
    health.reload();
  };

  if (projects.error) return <Alert kind="error">{projects.error}</Alert>;
  if (!projects.loading && list.length === 0 && !isAdmin)
    return <Empty>You are not assigned to any project yet. Ask an admin to add you.</Empty>;

  const groupName = (id: number | null) => selected?.groups.find((g) => g.id === id)?.name ?? '—';
  const summary = health.data;
  const problems = summary ? summary.levels.warning + summary.levels.critical : 0;

  return (
    <div>
      <PageHeader title="Devices">
        <ProjectPicker projects={list} value={unassigned ? 'unassigned' : String(projectId ?? '')} onChange={changeProject} showUnassigned={isAdmin} />
        <Button variant="secondary" onClick={refresh} aria-label="Refresh">
          <RefreshCw size={16} />
        </Button>
      </PageHeader>

      {summary && (problems > 0 || summary.failed_updates > 0) && (
        <div className="mb-4">
          <Alert kind="warning">
            <p className="font-medium">Check stability</p>
            <ul className="mt-1 list-disc pl-5">
              {problems > 0 && (
                <li>
                  {problems} device(s) rebooted unexpectedly more than allowed in the last {summary.window_hours} h
                  {summary.levels.critical > 0 && ` (${summary.levels.critical} critical)`}.
                </li>
              )}
              {summary.failed_updates > 0 && <li>{summary.failed_updates} device(s) failed to install their update.</li>}
              {summary.unstable_releases.map((r) => (
                <li key={`${r.project_id}-${r.version}`}>
                  {list.find((p) => p.id === r.project_id)?.name ?? 'Project'} {r.version}: {r.unhealthy} of {r.devices} device(s) unstable
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      )}

      {unassigned && (
        <div className="mb-4">
          <Alert kind="info">New devices appear here after their first check-in. Open a device to assign it to a project.</Alert>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          placeholder="Search serial or name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-64"
        />
        {selected && (
          <Select aria-label="Group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {selected.groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
        )}
        {selected && (
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button disabled={checked.size === 0} onClick={() => setUpdateScope('selected')}>
              <Rocket size={16} /> Update selected ({checked.size})
            </Button>
            <Button variant="secondary" disabled={!devices.data?.length} onClick={() => setUpdateScope('all')}>
              Update all
            </Button>
          </div>
        )}
      </div>

      {devices.error && <Alert kind="error">{devices.error}</Alert>}
      {!devices.loading && visible.length === 0 ? (
        <Empty>{unassigned ? 'No unassigned devices.' : 'No devices in this view.'}</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              {selected && (
                <th className="w-8">
                  <input type="checkbox" aria-label="Select all" checked={allChecked} onChange={toggleAll} />
                </th>
              )}
              <th>Device</th>
              {selected && <th>Group</th>}
              <th>Version</th>
              <th>Target</th>
              <th>State</th>
              <th>Health</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => (
              <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenDevice(d)}>
                {selected && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${d.serial_number}`} checked={checked.has(d.id)} onChange={() => toggle(d.id)} />
                  </td>
                )}
                <td>
                  <div className="font-medium">{d.name || d.serial_number}</div>
                  {d.name && <div className="text-xs text-slate-500">{d.serial_number}</div>}
                </td>
                {selected && <td>{groupName(d.group_id)}</td>}
                <td className="font-mono text-xs">{d.reported_version ?? '—'}</td>
                <td className="font-mono text-xs">
                  {d.effective_version ?? '—'}
                  {d.target_source === 'group' && <span className="ml-1 font-sans text-slate-400">(group)</span>}
                </td>
                <td>
                  <DeviceStateBadge state={d.state} />
                </td>
                <td>
                  <HealthBadge health={d.health} />
                </td>
                <td className="whitespace-nowrap text-slate-500">{timeAgo(d.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {openDevice && (
        <DeviceModal
          deviceId={openDevice.id}
          projects={list}
          onClose={() => setOpenDevice(null)}
          onChanged={refresh}
        />
      )}
      {updateScope && selected && (
        <UpdateModal
          project={selected}
          deviceIds={updateScope === 'selected' ? [...checked] : null}
          groupId={updateScope === 'all' && groupId ? Number(groupId) : null}
          deviceCount={
            updateScope === 'selected'
              ? checked.size
              : (devices.data ?? []).filter((d) => !groupId || String(d.group_id) === groupId).length
          }
          onClose={() => setUpdateScope(null)}
        />
      )}
    </div>
  );
}

function UpdateModal({
  project,
  deviceIds,
  groupId,
  deviceCount,
  onClose,
}: {
  project: Project;
  deviceIds: number[] | null;
  groupId: number | null;
  deviceCount: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const releases = useAsync(() => releasesApi.list(project.id), [project.id]);
  const active = (releases.data ?? []).filter((r) => r.status === 'active');
  const [releaseId, setReleaseId] = useState('');
  const [staged, setStaged] = useState(false);
  const [batchSize, setBatchSize] = useState('10');
  const [autoPause, setAutoPause] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const chosen = active.find((r) => String(r.id) === releaseId) ?? active[0];
  const scopeText = deviceIds
    ? `${deviceCount} selected device(s)`
    : groupId
      ? `all devices in group ${project.groups.find((g) => g.id === groupId)?.name}`
      : `all ${deviceCount} device(s) in ${project.name}`;

  const submit = async () => {
    if (!chosen) return;
    setSaving(true);
    setError(null);
    try {
      const job = await updatesApi.create(project.id, {
        release_id: chosen.id,
        ...(deviceIds ? { device_ids: deviceIds } : groupId ? { group_id: groupId } : { all: true }),
        batch_size: staged ? Number(batchSize) || null : null,
        auto_pause: autoPause,
        note,
      });
      navigate(`/updates?project=${project.id}&job=${job.id}`);
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  };

  return (
    <Modal title="Update firmware" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Target: {scopeText}.</p>
        {error && <Alert kind="error">{error}</Alert>}
        {!releases.loading && active.length === 0 ? (
          <Alert kind="warning">This project has no active release. Upload one on the Releases page first.</Alert>
        ) : (
          <>
            <Select label="Release" value={String(chosen?.id ?? '')} onChange={(e) => setReleaseId(e.target.value)}>
              {active.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.version}
                  {r.label ? ` – ${r.label}` : ''}
                </option>
              ))}
            </Select>
            {chosen && (
              <div className="max-h-32 overflow-y-auto rounded-md bg-slate-50 p-3 text-xs whitespace-pre-wrap text-slate-700">{chosen.changelog}</div>
            )}
            <Checkbox label="Staged rollout (update in batches)" checked={staged} onChange={(e) => setStaged(e.target.checked)} />
            {staged && (
              <Field
                label="Batch size"
                type="number"
                min={1}
                value={batchSize}
                onChange={(e) => setBatchSize(e.target.value)}
                hint="The next batch starts after the previous one finished and was observed for the soak time."
              />
            )}
            <Checkbox
              label="Pause automatically if updated devices start rebooting"
              checked={autoPause}
              onChange={(e) => setAutoPause(e.target.checked)}
            />
            <TextArea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={saving || !chosen}>
                {saving ? 'Starting…' : 'Start update'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
